import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import lottoService from '../../services/lotto/lottoService';
import './LottoStatisticsPanel.css';

/**
 * Component to display Lotto statistics for a wheel
 */
const LottoStatisticsPanel = ({ selectedWheel }) => {
  const { t } = useTranslation();
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('top');

  useEffect(() => {
    if (selectedWheel) {
      loadStatistics();
    }
  }, [selectedWheel]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const data = await lottoService.getWheelStatistics(selectedWheel.code, 10);
      setStatistics(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error loading statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedWheel) {
    return <div className="lotto-statistics-panel">Please select a wheel</div>;
  }

  if (loading) {
    return <div className="lotto-statistics-panel loading">Loading statistics...</div>;
  }

  if (error) {
    return (
      <div className="lotto-statistics-panel error">
        <p>Error: {error}</p>
        <button onClick={loadStatistics}>Retry</button>
      </div>
    );
  }

  if (!statistics) {
    return <div className="lotto-statistics-panel">No statistics available</div>;
  }

  return (
    <div className="lotto-statistics-panel">
      <h3>Statistics - {selectedWheel.name}</h3>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'top' ? 'active' : ''}`}
          onClick={() => setActiveTab('top')}
        >
          {t('topNumbers')}
        </button>
        <button
          className={`tab ${activeTab === 'cold' ? 'active' : ''}`}
          onClick={() => setActiveTab('cold')}
        >
          {t('coldNumbers')}
        </button>
        <button
          className={`tab ${activeTab === 'overdue' ? 'active' : ''}`}
          onClick={() => setActiveTab('overdue')}
        >
          {t('overdueNumbers')}
        </button>
        <button
          className={`tab ${activeTab === 'recommended' ? 'active' : ''}`}
          onClick={() => setActiveTab('recommended')}
        >
          {t('recommendedCombination')}
        </button>
        <button
          className={`tab ${activeTab === 'ai' ? 'active' : ''}`}
          onClick={() => setActiveTab('ai')}
        >
          {t('aiPrediction')}
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'top' && statistics.topNumbers && (
          <NumbersTable numbers={statistics.topNumbers} title={t('topFrequentNumbers')} />
        )}

        {activeTab === 'cold' && statistics.coldNumbers && (
          <NumbersTable numbers={statistics.coldNumbers} title={t('coldNumbersLeastFrequent')} />
        )}

        {activeTab === 'overdue' && statistics.overdueNumbers && (
          <OverdueNumbersTable numbers={statistics.overdueNumbers} />
        )}

        {activeTab === 'recommended' && (
          <div className="recommended-combination">
            <h4>{t('recommendedCombination')}</h4>
            {statistics.recommendedCombination && (
              <div className="combination-display">
                {statistics.recommendedCombination.map((num, idx) => (
                  <div key={idx} className="combination-number">
                    {num}
                  </div>
                ))}
              </div>
            )}
            <p className="combination-info">
              {t('basedOnCalculatedProbabilityMetrics')}
            </p>
          </div>
        )}

        {activeTab === 'ai' && (
          <div className="ai-prediction">
            <h4>{t('aiPrediction')}</h4>
            {statistics.predictAi ? (
              <div className="ai-prediction-display">
                <p className="ai-prediction-label">{t('aiPredictionNumbers')}:</p>
                <div className="combination-display">
                  {statistics.predictAi.split(' ').map((num, idx) => (
                    <div key={idx} className="combination-number" style={{ backgroundColor: '#4CAF50' }}>
                      {num}
                    </div>
                  ))}
                </div>
                <p className="ai-prediction-info">
                  {t('aiPredictionDescription')}
                </p>
              </div>
            ) : (
              <p className="text-muted">{t('noPredictionsAvailable')}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const NumbersTable = ({ numbers, title }) => {
  const { t } = useTranslation();
  return (
    <div className="numbers-table">
      <h4>{title}</h4>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>{t('number')}</th>
              <th>{t('frequency')}</th>
              <th>{t('delay')}</th>
              <th>{t('maxDelay')}</th>
            </tr>
          </thead>
          <tbody>
            {numbers.map((num, idx) => (
              <tr key={idx}>
                <td className="number-cell">{num.number}</td>
                <td>{num.frequency}</td>
                <td>{num.delay}</td>
                <td>{num.maxDelay}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const OverdueNumbersTable = ({ numbers }) => {
  const { t } = useTranslation();
  return (
    <div className="overdue-table">
      <h4>{t('overdueNumbersHighDelay')}</h4>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>{t('number')}</th>
              <th>{t('delay')}</th>
              <th>{t('maxDelay')}</th>
              <th>{t('frequency')}</th>
              <th>{t('trend')}</th>
            </tr>
          </thead>
          <tbody>
            {numbers.map((num, idx) => {
              const trend = num.delay > num.maxDelay * 0.8 ? t('critical') : t('highDelay');
              return (
                <tr key={idx} className={trend.toLowerCase()}>
                  <td className="number-cell">{num.number}</td>
                  <td className="delay-cell">{num.delay}</td>
                  <td>{num.maxDelay}</td>
                  <td>{num.frequency}</td>
                  <td className="trend-badge">{trend}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LottoStatisticsPanel;
